// Status enum, label and color mapping, plus shared formatters for the
// Paycheque list and Paycheque detail views.

export type PaychequeStatus = 'paid' | 'printed' | 'dd_sent' | 'pending' | 'voided';
export type PayMethod = 'cheque' | 'direct_deposit' | 'check';

export interface StatusColor {
  bg: string;
  fg: string;
}

export interface Paycheque {
  status?: PaychequeStatus;
  pay_method?: PayMethod;
  employee_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  [key: string]: unknown;
}

export const STATUS = {
  PAID: "paid",
  PRINTED: "printed",
  DD_SENT: "dd_sent",
  PENDING: "pending",
  VOIDED: "voided",
} as const;

export const STATUS_LABELS: Record<PaychequeStatus, string> = {
  paid: "Paid",
  printed: "Printed",
  dd_sent: "DD sent",
  pending: "Pending",
  voided: "Voided",
};

export const STATUS_COLORS: Record<PaychequeStatus, StatusColor> = {
  paid:    { bg: "#DCFCE7", fg: "#166534" },
  printed: { bg: "#DBEAFE", fg: "#1E40AF" },
  dd_sent: { bg: "#DCFCE7", fg: "#166534" },
  pending: { bg: "#FEF3C7", fg: "#92400E" },
  voided:  { bg: "#F3F4F6", fg: "#6B7280" },
};

export const PAY_METHOD_LABELS: Record<PayMethod, string> = {
  cheque: "Cheque",
  direct_deposit: "Dir. dep.",
  check: "Cheque",
};

export const PAY_METHOD_LABELS_LONG: Record<PayMethod, string> = {
  cheque: "Cheque",
  direct_deposit: "Direct deposit",
  check: "Cheque",
};

export function formatCurrency(value: number | string | null | undefined, currency?: string): string {
  if (value === null || value === undefined) return "";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "";
  try {
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: currency || "CAD" }).format(num);
  } catch (e) {
    return "$" + num.toFixed(2);
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}

export function formatDateLong(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

export function formatPeriodShort(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return "";
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return "";
  const sLabel = s.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return sLabel + " - " + e.getDate();
  }
  const eLabel = e.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  return sLabel + " - " + eLabel;
}

export function formatPeriodLong(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return "";
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return "";
  return s.toLocaleDateString("en-CA", { month: "long", day: "numeric" }) +
    " to " +
    e.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });
}

export function employeeNameFromPaycheque(pc: Paycheque | null | undefined): string {
  if (!pc) return "";
  if (pc.employee_name) return pc.employee_name;
  const last = (pc.last_name || "").trim();
  const first = (pc.first_name || "").trim();
  if (last && first) return last + ", " + first;
  return first || last || pc.email || "Unnamed";
}

export const isPending = (pc: Paycheque | null | undefined): boolean => Boolean(pc && pc.status === STATUS.PENDING);
export const isVoided = (pc: Paycheque | null | undefined): boolean => Boolean(pc && pc.status === STATUS.VOIDED);
export const isCheque = (pc: Paycheque | null | undefined): boolean => Boolean(pc && (pc.pay_method === "cheque" || pc.pay_method === "check"));
export const isDirectDeposit = (pc: Paycheque | null | undefined): boolean => Boolean(pc && pc.pay_method === "direct_deposit");
